'use client'

import { useParams } from 'next/navigation'
import { use, useState } from 'react'
import useSWR from 'swr'
import { useIsClient } from 'usehooks-ts'

import ActionWorldCard from '@/components/ActionWorldCard'
import ActionWorldCardSkeleton from '@/components/ActionWorldCardSkeleton'
import { RECOMMEND_WORLDS_LIMIT } from '@/constants/recommend'
import { useConfigsContext } from '@/context/ConfigsContext'
import { useUAContext } from '@/context/UAContext'
import { cn } from '@/lib/utils'
import { getRandomRecommendWorlds, Room } from '@/services/cms'

type RecommendWorldsProps = {
  recommendWorldsPromise: Promise<Room[] | undefined>
}

function RecommendWorlds({ recommendWorldsPromise }: RecommendWorldsProps) {
  const recommendWorlds = use(recommendWorldsPromise)

  const { deviceTypeWithAndroidAndIOS, isMobile } = useUAContext()
  const { store } = useConfigsContext()
  const { roomId } = useParams()

  const [doneRecommendWorlds, setDoneRecommendWorlds] = useState(false)

  const htcToken = store?.credentials?.htcToken

  const { data, isLoading: isLoadingRecommendWorlds } = useSWR(
    htcToken ? `getRecommendWorlds-${roomId}` : null,
    () =>
      getRandomRecommendWorlds(htcToken, {
        device: deviceTypeWithAndroidAndIOS,
        limit: RECOMMEND_WORLDS_LIMIT,
      }),
    {
      // 🥷🏼 we don't want to revalidate on focus
      // 🥷🏼 because it doesn't need to be updated frequently
      revalidateOnFocus: false,
      fallbackData: recommendWorlds,
      onSuccess: () => {
        setDoneRecommendWorlds(true)
      },
    },
  )

  const isLoading = htcToken ? !doneRecommendWorlds : isLoadingRecommendWorlds
  const isClient = useIsClient()

  return (
    <>
      {isClient &&
        isLoading &&
        Array.from({ length: RECOMMEND_WORLDS_LIMIT }).map((_, index) => (
          <ActionWorldCardSkeleton
            key={index}
            className={cn('md:min-w-[200px]', {
              '1.5xl:min-w-[240px] 3xl:min-w-[306px] aspect-[1.72] w-full min-w-[200px] md:min-w-[218px] lg:min-w-[200px]':
                !isMobile,
            })}
          />
        ))}
      {data?.map((world) => (
        <div key={world.id}>
          <ActionWorldCard
            className={cn('max-w-[200px] min-w-[200px]', {
              '1.5xl:min-w-[240px] 1.5xl:max-w-[240px] 3xl:min-w-[306px] 3xl:max-w-auto md:max-w-auto aspect-[1.72] w-full max-w-[200px] min-w-[200px] md:max-w-full md:min-w-[218px] lg:max-w-[200px] lg:min-w-[200px]':
                !isMobile,
            })}
            info={world}
            hideLiked
          />
        </div>
      ))}
    </>
  )
}

export default RecommendWorlds
